/**
 * Download link for a tool result.
 *
 * Hosted results live on the content bucket's own origin, where the `download` attribute is ignored
 * and a click only opens the image. The click fetches the bytes instead (the bucket's CORS admits
 * every product origin) and saves them under the real format's extension; if the fetch fails, the
 * image opens in a new tab so the user can still keep it.
 */
import type {ReactNode} from 'react'

const EXT: Record<string, string> = {'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp'}

function extFor(type: string, url: string): string {
    return EXT[type] ?? url.match(/\.(webp|png|jpe?g)(?:$|\?)/i)?.[1].replace('jpeg', 'jpg') ?? 'webp'
}

function save(href: string, filename: string) {
    const a = document.createElement('a')
    a.href = href
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
}

/**
 * Fetch the bytes and save them under the real format's extension; if the fetch fails, open the
 * image in a new tab so the reader can still keep it. Never rejects.
 */
export async function downloadUrl(url: string, basename: string): Promise<void> {
    try {
        const res = await fetch(url, {mode: 'cors', cache: 'no-store'})
        if (!res.ok) throw new Error(String(res.status))
        const blob = await res.blob()
        const objectUrl = URL.createObjectURL(blob)
        save(objectUrl, `${basename}.${extFor(blob.type, url)}`)
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000)
    } catch {
        window.open(url, '_blank', 'noopener')
    }
}

export function DownloadLink({url, basename, className, onDownload, children}: {
    url: string
    /** Filename without extension. */
    basename: string
    className?: string
    onDownload?: () => void
    children: ReactNode
}) {
    const isInline = url.startsWith('data:')
    const inlineName = isInline ? `${basename}.${extFor(url.slice(5, url.indexOf(';')), '')}` : undefined

    const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
        onDownload?.()
        if (isInline) return
        e.preventDefault()
        await downloadUrl(url, basename)
    }

    return (
        <a href={url} download={inlineName} className={className} onClick={handleClick}>
            {children}
        </a>
    )
}
